import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  type DropdownMenuItemProps,
  IconButton,
  Text,
  TextField,
} from '@fly/core';
import { FlyOutsideClickHandler } from '@fly/shared-ui';
import cn from 'classnames';

import styles from './EditableText.module.less';

export type EditableTextProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'onSubmit' | 'onChange'
> & {
  defaultValue: string;
  /** When set, empty/cancel revert uses this instead of `defaultValue`. */
  revertValue?: string;
  onSubmit: (value: string) => void;
  onChange?: (value: string) => void;
  TextWrapper?: React.ComponentType<{ children: string }>;
  /**
   * When set (including `[]`), a overflow menu is shown (`dots_vertical` trigger).
   * The first row is always "edit" and calls the same logic as the default pencil button.
   */
  menuItems?: DropdownMenuItemProps[];
  /** Label for the fixed first menu row (edit). */
  editMenuItemTitle?: string;
  /**
   * Extra classes for the overflow-menu trigger. Use to reveal it on a parent
   * group hover (e.g. `group-hover/release-item:block`) instead of only on
   * this component's own hover.
   */
  menuTriggerClassName?: string;
};

export function EditableText({
  defaultValue,
  revertValue,
  onChange,
  onSubmit,
  TextWrapper,
  menuItems = [],
  editMenuItemTitle,
  menuTriggerClassName,
  ...rest
}: EditableTextProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState(defaultValue);
  const [isEditing, setIsEditing] = useState(false);
  /** Mirrors menu open state so the trigger stays interactive while open (see actionClassName). */
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const resolvedEditMenuItemTitle =
    editMenuItemTitle ??
    t('app.common.actions.edit', {
      defaultValue: 'Edit',
    });
  const resolvedRevertValue = revertValue ?? defaultValue;

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (!isEditing) {
      setOverflowMenuOpen(false);
    }
  }, [isEditing]);

  const spanRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [width, setWidth] = useState('auto');

  useLayoutEffect(() => {
    if (spanRef.current) {
      // Extra padding to account for input border + text indent
      setWidth(`${spanRef.current.offsetWidth + 16}px`);
    }
  }, [value, isEditing]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onChange?.(e.target.value);
    },
    [onChange]
  );

  const submittingRef = useRef(false);

  const handleSubmit = useCallback(() => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    setIsEditing(false);

    if (!value.trim()) {
      setValue(resolvedRevertValue);
      onSubmit(resolvedRevertValue);
      submittingRef.current = false;
      return;
    }

    if (value === defaultValue) {
      submittingRef.current = false;
      return;
    }
    onSubmit(value);
    submittingRef.current = false;
  }, [onSubmit, value, defaultValue, resolvedRevertValue]);

  const beginEditing = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setOverflowMenuOpen(false);
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  return (
    <div
      {...rest}
      className='group min-w-0 relative inline-flex items-center overflow-hidden'
    >
      {isEditing ? (
        <FlyOutsideClickHandler onOutsideClick={handleSubmit}>
          <TextField
            size='3'
            variant='surface'
            value={value}
            onChange={handleChange}
            className={cn(
              styles.input,
              'min-w-10 h-6 p-0 edit-text-bg font-semibold rounded-fly-3 bg-transparent indent-fly-3 shadow-none transition-opacity',
              'group-focus-within:opacity-100! group-hover:opacity-50'
            )}
            style={{ width }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onBlur={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                inputRef.current?.blur();
              }
              if (e.key === 'Escape') {
                setValue(defaultValue);
                setTimeout(() => {
                  inputRef.current?.blur();
                });
              }
            }}
            ref={inputRef}
          />
        </FlyOutsideClickHandler>
      ) : (
        <Text size='3' as='p' className='font-semibold truncate px-fly-1'>
          {TextWrapper ? <TextWrapper>{value}</TextWrapper> : value}
        </Text>
      )}
      {/* Invisible mirroring text for width calculation */}
      <Text
        size='3'
        ref={spanRef}
        className={cn(
          'font-semibold invisible absolute overflow-hidden px-fly-1 whitespace-pre',
          !isEditing && 'max-w-full'
        )}
      >
        {value}
      </Text>
      {!isEditing ? (
        <DropdownMenu
          hideContentWhenClosed
          onOpenChange={setOverflowMenuOpen}
          trigger={
            <IconButton
              icon='dots_vertical'
              size='1'
              color='neutral'
              variant='ghost'
              className={cn(
                'h-6 w-6 shrink-0',
                overflowMenuOpen
                  ? 'block shrink-0'
                  : 'hidden shrink-0 group-hover:block',
                !overflowMenuOpen && menuTriggerClassName
              )}
            />
          }
          items={[
            {
              title: resolvedEditMenuItemTitle,
              iconStart: 'pencil',
              onClick: (e) => beginEditing(e),
            },
            ...menuItems,
          ]}
        />
      ) : null}
    </div>
  );
}
